import React, { createContext, useState, useEffect } from "react";
// import { Modal, Drawer } from "antd";
import { v4 as uuidv4 } from "uuid";
import { Button } from "components/Button";
import { BTN_SIZE, BTN_VARIANTS } from "constants/components";
import { ModalType } from "models/common";
import asyncComponentLoader from "core/loader";

// Type definition for modal configuration
type ModalConfig = {
  id: string;
  type: ModalType;
  uId?: string;
  title?: string;
  children?: React.ReactNode;
  onOk?: () => void;
  onCancel?: () => void;
  okText?: string;
  cancelText?: string;
  showFooter?: boolean;
  width?: string;
  closable?: boolean;
  isDrawer?: boolean; // New parameter to determine if it should be a drawer
  drawerPlacement?: "top" | "right" | "bottom" | "left"; // Drawer placement
  className?: string;
  preventModalStacking?: boolean; // To determine if same modal stacking above one other is allowed
};

// Context type to manage modal state
type ModalContextType = {
  showModal: (config: Omit<ModalConfig, "id">) => string;
  closeModal: (id: string) => void;
};
const Modal = asyncComponentLoader(() =>
  import("antd").then((module) => ({ default: module.Modal })),
);
const Drawer = asyncComponentLoader(() =>
  import("antd").then((module) => ({ default: module.Drawer })),
);
// Create a context to handle modal actions
export const ModalContext = createContext<ModalContextType | null>(null);

// Custom hook to detect screen size
const useScreenSize = () => {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenWidth;
};

// ModalProvider component to wrap the app and manage modals
export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modals, setModals] = useState<ModalConfig[]>([]);
  const screenWidth = useScreenSize();

  // Show a modal by adding it to the modals state
  const showModal = (config: Omit<ModalConfig, "id">) => {
    const id =
      config?.type === ModalType.CUSTOM ? (config?.uId ?? uuidv4()) : uuidv4();

    setModals((prev) => {
      const existingIndex = prev.findIndex((modal) => modal.id === id);
      if (existingIndex !== -1) {
        // Modal with this ID already exists, don't add duplicate
        return prev;
      }
      return [...prev, { id, ...config }];
    });
    return id; // Generate a unique ID for the modal
  };

  // Close a modal by filtering it out of the state
  const closeModal = (id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  };

  // Function to determine if should render as drawer
  const shouldRenderAsDrawer = (isDrawer: boolean) => {
    return isDrawer && screenWidth <= 768;
  };

  // Function to render footer buttons
  const renderFooterButtons = (
    onOk?: () => void,
    onCancel?: () => void,
    okText?: string,
    cancelText?: string,
    id?: string,
  ) => [
    <Button
      key="ok"
      type={BTN_VARIANTS.PRIMARY}
      size={BTN_SIZE.DEFAULT}
      onClick={() => {
        onOk?.();
        closeModal(id!);
      }}
    >
      {okText}
    </Button>,
    cancelText && (
      <Button
        key="cancel"
        type={BTN_VARIANTS.SECONDARY}
        size={BTN_SIZE.DEFAULT}
        onClick={() => {
          onCancel?.();
          closeModal(id!);
        }}
      >
        {cancelText}
      </Button>
    ),
  ];

  return (
    <ModalContext.Provider value={{ showModal, closeModal }}>
      {children} {/* Render child components */}
      {/* Render each modal or drawer */}
      {modals.map((config) => {
        const {
          id,
          title,
          children: modalChildren,
          onOk,
          onCancel,
          okText,
          cancelText,
          showFooter = false,
          width,
          closable = true,
          isDrawer = false,
          drawerPlacement = "bottom",
          className,
        } = config;

        // Determine if should render as drawer based on isDrawer flag and screen size
        if (shouldRenderAsDrawer(isDrawer)) {
          return (
            <Drawer
              key={id}
              open
              title={title}
              placement={drawerPlacement}
              closable={false}
              onClose={() => {
                onCancel?.();
                closeModal(id);
              }}
              maskClosable={false}
              height="95%"
              className={className}
            >
              <div style={{ textAlign: "right" }}>
                <Button
                  onClick={() => {
                    onCancel?.();
                    closeModal(id);
                  }}
                  className="drawer_cancel"
                >
                  Cancel
                </Button>
              </div>

              {modalChildren}
            </Drawer>
          );
        }

        // Render as Modal (default behavior or when screen > 768px)
        return (
          <Modal
            key={id}
            open
            title={title}
            centered
            maskClosable={false}
            onCancel={() => {
              onCancel?.();
              closeModal(id);
            }}
            onOk={() => {
              onOk?.();
              closeModal(id);
            }}
            width={width ?? undefined}
            closable={closable}
            footer={
              showFooter
                ? renderFooterButtons(onOk, onCancel, okText, cancelText, id)
                : null
            }
            classNames={{
              content: className,
            }}
          >
            {modalChildren}
          </Modal>
        );
      })}
    </ModalContext.Provider>
  );
};
